import { Dialog } from '@ymba/ym-ui';
import axios from 'axios';
import EventHandler from '@/utils/event';
import {
  storageStartPlayTime,
  getStorageStartPlayTime,
  removeStorageStartPlayTime,
  storagePlayedTimeRange,
  getStoragePlayedTimeRange,
  removeStoragePlayTimeRange,
  durationToTime,
  storagePlayTimeDuration,
} from '@/utils';
import { MEDIA_ERROR_CODE } from '@/utils/constant';
// import Monitor from '@/common/monitor';

function playAudio(audio) {
  return new Promise((resolve, reject) => {
    const promise = audio.play();
    if (promise != null) {
      promise.then(() => resolve()).catch(e => reject(e));
    }
    return resolve();
  });
}

function playAudioWithWXJSBridge(audio) {
  return new Promise((resolve, reject) => {
    window.WeixinJSBridge.invoke('getNetworkType', {}, async () => {
      try {
        await playAudio(audio);
        return resolve();
      } catch (e) {
        return reject(e);
      }
    }, false);
  });
}

function autoPlayAudio(audio) {
  return new Promise(async (resolve, reject) => {
    const { WeixinJSBridge } = window;
    if (WeixinJSBridge) {
      return playAudioWithWXJSBridge(audio);
    }
    document.addEventListener('WeixinJSBridgeReady', () => playAudioWithWXJSBridge(audio), false);
    try {
      await playAudio(audio);
      return resolve();
    } catch (e) {
      return reject(e);
    }
  });
}

class AudioPlayer {
  constructor(options) {
    const { logHandler } = options;
    // 事件处理器
    this.eventHandler = new EventHandler();
    // 事件列表
    this.events = [
      'abort',
      'canplay',
      'canplaythrough',
      'durationchange',
      'emptied',
      'ended',
      'error',
      'loadeddata',
      'loadedmetadata',
      'loadstart',
      'pause',
      'play',
      'playing',
      'progress',
      'ratechange',
      'seeked',
      'seeking',
      'stalled',
      'suspend',
      'timeupdate',
      'volumechange',
      'waiting',
    ];

    // 初始化播放器
    this.initPlayer();

    // 设置可用播放资源码
    this.setupCodecs();

    // log处理器
    this.logHandler = logHandler;

    // 播放ID
    this.playingId = 0;

    // 暂停ID
    this.pauseId = 0;

    // 结束ID
    this.endedId = 0;

    // 当前资源对象
    this.sourceObject = null;

    // 获取环境
    this.env = process.env.NODE_ENV;

    // sentry 打log
    // if (this.env === 'production') {
    //   this.sentry = Monitor.getInstance({
    //     environment: process.env.VUE_APP_PACK_MODE,
    //   });
    // }
  }

  /**
   * 设置播放ID
   * @param value
   */
  setPlayingId(value) {
    this.playingId = value;
  }

  /**
   * 设置暂停ID
   * @param value
   */
  setPauseId(value) {
    this.pauseId = value;
  }

  /**
   * 设置结束ID
   * @param value
   */
  setEndedId(value) {
    this.endedId = value;
  }

  /**
   * 设置资源对象
   * @param value
   */
  setSourceObject(value) {
    this.sourceObject = value;
  }

  /**
   * 设置当前播放时间
   * @param time
   */
  setCurrentTime(time) {
    const { audio } = this;
    if (audio == null) return;
    audio.currentTime = time;
  }

  /**
   * 获取播放时间
   * @returns {*}
   */
  getCurrentTime() {
    const { audio } = this;
    if (audio == null) return 0;
    return audio.currentTime;
  }

  /**
   * 向sentry 打log
   * @param data
   * @param type
   * @param message
   */
  logSentry(data, type = 'error', message) {
    const { sentry } = this;
    if (sentry == null) return;
    this.sentry.log(data, type, message);
  }

  /**
   * 设置可播资源
   */
  setupCodecs() {
    let audioTest = null;
    try {
      audioTest = (typeof Audio !== 'undefined') ? new Audio() : null;
    } catch (e) {
      return;
    }

    if (!audioTest || typeof audioTest.canPlayType !== 'function') {
      return;
    }

    const mpegTest = audioTest.canPlayType('audio/mpeg;').replace(/^no$/, '');

    // Opera version <33 has mixed MP3 support, so we need to check for and block it.
    const checkOpera = window.navigator.userAgent.match(/OPR\/([0-6].)/g);
    const isOldOpera = (checkOpera && parseInt(checkOpera[0].split('/')[1], 10) < 33);

    this.codecs = {
      mp3: !!(!isOldOpera && (mpegTest || audioTest.canPlayType('audio/mp3;').replace(/^no$/, ''))),
      mpeg: !!mpegTest,
      opus: !!audioTest.canPlayType('audio/ogg; codecs="opus"').replace(/^no$/, ''),
      ogg: !!audioTest.canPlayType('audio/ogg; codecs="vorbis"').replace(/^no$/, ''),
      oga: !!audioTest.canPlayType('audio/ogg; codecs="vorbis"').replace(/^no$/, ''),
      wav: !!audioTest.canPlayType('audio/wav; codecs="1"').replace(/^no$/, ''),
      aac: !!audioTest.canPlayType('audio/aac;').replace(/^no$/, ''),
      caf: !!audioTest.canPlayType('audio/x-caf;').replace(/^no$/, ''),
      m4a: !!(audioTest.canPlayType('audio/x-m4a;') || audioTest.canPlayType('audio/m4a;') || audioTest.canPlayType('audio/aac;')).replace(/^no$/, ''),
      mp4: !!(audioTest.canPlayType('audio/x-mp4;') || audioTest.canPlayType('audio/mp4;') || audioTest.canPlayType('audio/aac;')).replace(/^no$/, ''),
      weba: !!audioTest.canPlayType('audio/webm; codecs="vorbis"').replace(/^no$/, ''),
      webm: !!audioTest.canPlayType('audio/webm; codecs="vorbis"').replace(/^no$/, ''),
      dolby: !!audioTest.canPlayType('audio/mp4; codecs="ec-3"').replace(/^no$/, ''),
      flac: !!(audioTest.canPlayType('audio/x-flac;') || audioTest.canPlayType('audio/flac;')).replace(/^no$/, ''),
      m3u8: !!audioTest.canPlayType('application/vnd.apple.mpegURL;').replace(/^no$/, ''),
    };
  }

  /**
   * 初始化播放器
   */
  initPlayer() {
    // 初始化音频播放器
    this.audio = new Audio();

    // add event listener
    this.addEventListener();
  }

  /**
   * 获取可播资源
   * @param object
   * @returns {*}
   */
  getSupportSource(object) {
    // Throw an error if no source is provided.
    const { source } = object;
    if (!source || !source.mp3 || source.mp3.length === 0) {
      this.eventHandler.emit('playerEmptySource');
      return '';
    }

    const { mp3 } = source;
    const sourceArray = (typeof mp3 === 'string') ? [mp3] : mp3;

    const src = sourceArray.find((item) => {
      let ext = /^data:audio\/([^;,]+);/i.exec(item);
      if (!ext) {
        ext = /\.([^.]+)$/.exec(item.split('?', 1)[0]);
      }
      if (ext) {
        ext = ext[1].toLowerCase();
      }
      if (this.codecs && this.codecs[ext]) {
        return item;
      }
      return false;
    });

    if (!src) {
      this.eventHandler.emit('playerNoSupportedSource');
      return '';
    }

    return src;
  }

  /**
   * 播放数打点
   */
  logPlayCount() {
    return new Promise(async (resolve, reject) => {
      // 获取当前资源
      const { sourceObject } = this;
      // 当前资源不存在则退出
      if (sourceObject == null) return;
      // 获取当前资源ID，类型
      const { id, type, audioId } = sourceObject;
      try {
        const data = await this.logHandler.logPlayCount(id, type, audioId);
        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * 播放进度打点
   */
  logPlayProgress() {
    return new Promise(async (resolve, reject) => {
      // 获取当前资源
      const { sourceObject } = this;
      // 当前资源不存在则退出
      if (sourceObject == null) return;
      // 获取当前资源ID，时长，类型
      const {
              id,
              duration: time,
              type,
              audioId,
      } = sourceObject;
      // 获取已缓存播放时间段
      const range = getStoragePlayedTimeRange(id, type);
      if (range.length > 0) {
        // 获取播放器时长
        const { audio } = this;
        let { duration } = audio;
        if (!duration && Number.isNaN(duration)) {
          // 转换时长
          duration = durationToTime(time);
        }
        // 收集打点数据
        const params = {
          id,
          type,
          duration,
          range,
          audioId,
        };
        try {
          const data = await this.logHandler.logPlayProgress(id, params);
          removeStoragePlayTimeRange(id, type);
          resolve(data);
        } catch (e) {
          console.log('catch e', e);
          reject(e);
        }
      }
    });
  }

  /**
   * 缓存开始播放时间
   */
  storageStartTime() {
    // 获取当前资源
    const { sourceObject } = this;
    // 当前资源不存在则退出
    if (sourceObject == null) return;
    // 获取当前资源ID，时长
    const { id, type } = sourceObject;
    // 获取时间
    const time = Math.floor(this.getCurrentTime());
    // 记录播放开始时间
    storageStartPlayTime(id, type, time);
  }

  /**
   * 缓存已播时间段
   */
  async storageTimeRange() {
    // 获取当前资源
    const { sourceObject } = this;
    // 当前资源不存在则退出
    if (sourceObject == null) return;
    // 获取当前资源ID，时长
    const { id, type } = sourceObject;
    // 获取已缓存时间段
    const range = getStoragePlayedTimeRange(id, type);
    // 获取已缓存的开始播放时间
    const start = getStorageStartPlayTime(id, type);
    // 获取当前时间
    const end = this.getCurrentTime();
    if (end > 0 && end > start) {
      // 数据
      const data = {
        start,
        end,
      };
      // 增加时间段
      range.push(data);
      // 缓存至本地
      storagePlayedTimeRange(id, type, range);
      // 更新已播时间
      storageStartPlayTime(id, type, Math.floor(end));
    }
  }

  switchAudio(object) {
    const { audio, sourceObject, endedId } = this;
    if (sourceObject != null) {
      const { id } = sourceObject;
      if (id !== endedId) {
        // 上一首音频进行打点
        this.logPlayProgress();
      }
    }

    // 获取音频资源
    const src = this.getSupportSource(object);
    // 没有可播音频则结束函数
    if (src === '') {
      return;
    }

    // 获取当前音频ID／类型／时长
    const { id, type, duration } = object;
    // 记录音频时长
    storagePlayTimeDuration(id, type, duration);

    // https://stackoverflow.com/questions/28905253/media-err-decode-on-html5-video-in-ios-uiwebview-after-many-plays
    // 重置资源链接
    audio.src = '';

    // 重载音频播放器
    audio.load();

    // 设置播放资源
    audio.src = src;

    // setup event listeners
    this.setupEventListener(object);

    // 本地缓存已播时间
    const storageTime = getStorageStartPlayTime(id, type) || 0;
    // 跳转播放时间
    this.seek(storageTime);
  }

  /**
   * 播放
   * @param object
   */
  play(object) {
    const { audio } = this;
    if (audio == null) return;

    // 获取播放ID
    const { playingId } = this;
    // 获取新资源ID
    const { id, type } = object;

    // 资源未更新，则return 函数
    if (playingId === id) return;

    // 缓存其他音频已播时间
    if (!audio.paused) {
      // 本地缓存上一首进度
      this.storageTimeRange();
    }

    // 获取暂停ID
    const { pauseId } = this;

    if (pauseId !== id) {
      this.switchAudio(object);
    } else {
      const storageTime = getStorageStartPlayTime(id, type) || 0;
      if (storageTime) this.seek(storageTime);
    }

    // 播放音频
    const promise = autoPlayAudio(audio);

    if (promise != null) {
      promise.then().catch((e) => {
        // 重置播放ID
        this.setPlayingId(0);

        // 暂停音频
        audio.pause();

        // 触发错误监控
        this.logSentry(e, 'error', 'audio play promise error');
      });
    }

    // 设置资源对象
    this.setSourceObject(object);
  }

  /**
   * 暂停
   */
  pause() {
    const { audio } = this;
    if (audio == null) return;
    audio.pause();
  }

  /**
   * 设置播放速率
   * @param rate
   */
  rate(rate) {
    const { audio } = this;
    if (audio == null) return;
    audio.playbackRate = rate;
  }

  /**
   * 设置播放时间
   * @param time
   */
  seek(time) {
    const { audio } = this;
    if (audio == null) return;
    // 跳转前，收集时间段
    this.storageTimeRange();
    // 获取可跳转时间段
    const { seekable } = audio;
    const { length } = seekable;
    if (length > 0) {
      let seekableEnd = seekable.end(0);
      Array.from(Array(length)).forEach((i, index) => {
        seekableEnd = seekable.end(index);
      });
      if (seekableEnd >= time) {
        this.setCurrentTime(time);
      }
      return;
    }
    // set currentTime before metadate load will fail on iOS
    this.once('loadedmetadata', () => {
      this.setCurrentTime(time);
    });
  }

  /**
   * 添加事件监控
   */
  addEventListener() {
    this.events.forEach((key) => {
      this.audio.addEventListener(`${key}`, (e) => {
        const { sourceObject } = this;
        const fn = this[`on${key}`];
        if (fn && typeof fn === 'function') {
          fn.call(this, e, sourceObject);
        }
        this.eventHandler.emit(`${key}`, e, sourceObject);
      }, false);
    });
  }

  /**
   * 设置事件监控
   * @param object
   */
  setupEventListener(object) {
    this.events.forEach((key) => {
      const fn = object[`on${key}`];
      if (fn && typeof fn === 'function') {
        this.on(key, fn);
      }
    });
  }

  /**
   * 播放错误
   */
  async onerror(e = {}) {
    const { audio } = this;
    if (audio == null) {
      // 发送错误监控
      this.logSentry(new Error('player not exist'), 'error', 'AudioPlayError');
      return;
    }

    // 重置播放ID
    this.setPlayingId(0);
    // 重置暂停ID
    this.setPauseId(0);

    // 获取错误信息
    const { error = {} } = audio;
    const { code = 0 } = error || {};
    const { currentSrc = '' } = audio;
    let responseError = '';
    let response = '';
    if (currentSrc !== '') {
      try {
        const res = await axios({
          url: currentSrc,
        });
        const { status, statusText, headers } = res;
        response = JSON.stringify({ status, statusText, headers });
      } catch (requestError) {
        responseError = requestError;
      }
    }

    // 解析错误原因
    const errorReason = MEDIA_ERROR_CODE[code] || {};
    const {
      name: MediaErrorName = '',
      message: MediaErrorMessage = '',
    } = errorReason;

    const { message: errorFunctionCatchMessage = '' } = e;
    const currentTime = this.getCurrentTime();

    // 获取当前资源对象
    const { sourceObject } = this;

    // 发送错误监控
    const errorLogParams = {
      sourceObject,
      currentSrc,
      currentTime,
      errorFunctionCatchMessage,
      response,
      responseError,
      MediaErrorName,
      MediaErrorMessage,
    };
    this.logSentry(errorLogParams, 'error', 'AudioPlayError');
    // 提示用户
    if (MediaErrorMessage !== '') {
      Dialog.alert({ message: '网络出现问题，请稍后再试' });
    }
  }

  /**
   * 播放被中断
   */
  onabort() {
    // 重置播放ID
    this.setPlayingId(0);
    // 重置暂停ID
    this.setPauseId(0);
    // 收集时间段
    this.storageTimeRange();
  }

  /**
   * 开始播放
   */
  onplay() {
    // 收集开始时间
    this.storageStartTime();
    // 获取暂停ID
    const { pauseId, sourceObject } = this;
    // 获取当前ID, 类型
    const { id, type } = sourceObject;
    // 设置播放ID
    this.setPlayingId(id);
    // 仅有program 类型需要记录播放数
    if (pauseId !== id && type === 'program') {
      this.logPlayCount();
    }
  }

  /**
   * 跳转结束
   */
  onseeked() {
    // 收集开始时间
    this.storageStartTime();
  }

  /**
   * 播放暂停
   * @param e
   * @param object
   */
  onpause(e, object) {
    const { id } = object;
    // 重置播放ID
    this.setPlayingId(0);
    // 设置暂停ID
    this.setPauseId(id);
    // 收集时间段
    this.storageTimeRange();
  }

  /**
   * 播放结束
   * @param e
   * @param object
   */
  onended(e, object) {
    // 重置播放ID
    this.setPlayingId(0);
    // 重置暂停ID
    this.setPauseId(0);

    // 删除开始播放时间缓存
    const { id, type } = object;
    removeStorageStartPlayTime(id, type);

    // 设置结束ID
    this.setEndedId(id);

    // 收集播放进度
    this.logPlayProgress().then((data) => {
      // 触发本章学习完成事件
      this.eventHandler.emit('lessonComplete', {
        id,
        data,
      });
    });
  }

  /**
   * 事件监控
   * @param type
   * @param fn
   */
  on(type, fn) {
    return this.eventHandler.on(type, fn);
  }

  /**
   * 只监控事件一次
   * @param type
   * @param fn
   * @returns {*}
   */
  once(type, fn) {
    return this.eventHandler.once(type, fn);
  }

  /**
   * 取消事件监控
   * @param type
   * @param fn
   * @returns {undefined}
   */
  off(type, fn) {
    return this.eventHandler.off(type, fn);
  }
}

export default AudioPlayer;
